import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getLead, getInteractionTemplates, getRecommendedContacts,
  previewEmail, improveEmail, autoImproveEmail, translateEmailPreview, sendEmail,
  getAiProviders, personalizeEmail, runEmailChain,
  getCurrentUser, getAiPromptTemplatesByCategory,
  generateLeadOutreachHint,
  saveEmailDraft, scheduleEmail, getEmailQueueItem, sendQueuedEmailNow,
} from '../services/api';
import {
  Box, Typography, Button, TextField, MenuItem, Paper, Alert, Grid,
  CircularProgress, DialogActions, DialogContent, DialogTitle, Dialog, Chip, Stack, ListSubheader, Tooltip,
  IconButton, Menu, ListItemIcon, ListItemText, Divider, Collapse,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TranslateIcon from '@mui/icons-material/Translate';
import SendIcon from '@mui/icons-material/Send';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import RichEmailEditor from '../components/RichEmailEditor';
import Modal from '../components/Modal';

// Detect whether a target-language label should render right-to-left.
// Matches by lowercase substring so "Hebrew", "he", "עברית" all hit.
const RTL_TOKENS = [
  'he', 'iw', 'hebrew', 'עברית',
  'ar', 'arabic', 'العربية',
  'fa', 'persian', 'farsi', 'فارسی',
  'ur', 'urdu', 'اردو',
  'yi', 'yiddish',
];
function isRtlLanguage(label) {
  if (!label) return false;
  const lower = label.toLowerCase();
  return RTL_TOKENS.some(t => lower.includes(t.toLowerCase()));
}

// Resolves {{Placeholder}} tokens in template/AI-generated content.
// Server already substitutes these on previewEmail/send paths, but AI rewrites
// (Improve / Personalize / Chain / Translate) can re-emit raw placeholders, so
// we resolve them client-side at every external-content entry point.
const EMAIL_BASE_STYLES = `
  html, body { margin: 0; padding: 16px; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; line-height: 1.6; color: #444; }
  img { max-width: 100%; height: auto; }
  a { color: #4a90e2; }
  table { border-collapse: collapse; }
`;

function buildPreviewDoc(html) {
  if (!html) return `<!doctype html><html><head><style>${EMAIL_BASE_STYLES}</style></head><body></body></html>`;
  if (/<!doctype/i.test(html)) return html;
  return `<!doctype html><html><head><meta charset="utf-8"><style>${EMAIL_BASE_STYLES}</style></head><body>${html}</body></html>`;
}

function substitutePlaceholdersImpl(html, lead, contact, user) {
  if (!html) return '';
  const map = {};
  const set = (k, v) => { map[k.toLowerCase()] = v ?? ''; };

  if (lead) {
    set('companyName', lead.companyName);
    set('website', lead.website);
    set('country', lead.country);
    set('leadEmail', lead.email);
    set('industry', lead.industryName || lead.industry?.name || lead.industry);
  }
  if (contact) {
    const first = contact.firstName ?? '';
    const last = contact.lastName ?? '';
    set('firstName', first);
    set('lastName', last);
    set('fullName', `${first} ${last}`.trim());
    set('jobTitle', contact.jobTitle);
    set('contactEmail', contact.email);
    set('contactFirstName', first);
    set('contactLastName', last);
    set('contactJobTitle', contact.jobTitle);
  }
  if (user) {
    const name = user.fullName || user.username || '';
    set('senderName', name);
    set('senderTitle', user.jobTitle);
    set('senderEmail', user.email);
    set('senderSignature', user.emailSignature);
  }

  return html.replace(/\{\{(\w+)\}\}/g, (m, key) => {
    const v = map[key.toLowerCase()];
    return v != null ? v : m;
  });
}

// Wraps the user's email signature in a marker container so we can find it
// unambiguously across AI rewrites, translation passes, and editor round-trips.
// Backend (AppendSignatureIfMissing / SplitSignature) keys off the same attribute.
function wrapSignature(signatureHtml) {
  return `<div data-signature="true">${signatureHtml || ''}</div>`;
}

function bodyHasSignatureMarker(html) {
  return !!html && /data-signature\s*=/i.test(html);
}

// True when the rendered text of `body` already includes the first 40 non-whitespace
// chars of the user's signature (e.g. template injected via {{senderSignature}}).
function bodyContainsSignatureFingerprint(body, signature) {
  if (!body || !signature) return false;
  const fp = String(signature).replace(/<[^>]+>/g, '').replace(/\s+/g, '').slice(0, 40).toLowerCase();
  if (!fp) return false;
  const stripped = String(body).replace(/<[^>]+>/g, '').replace(/\s+/g, '').toLowerCase();
  return stripped.includes(fp);
}

function ensureSignature(body, signature) {
  if (!signature) return body || '';
  const html = body || '';
  if (bodyHasSignatureMarker(html)) return html;
  if (bodyContainsSignatureFingerprint(html, signature)) return html;
  const sep = html.trimEnd().endsWith('>') ? '<br><br>' : '\n<br><br>';
  return (html.trimEnd() + sep + wrapSignature(signature));
}

// Splits body into [prose, signatureHtml]. Returns ['', body] only if everything is signature;
// returns [body, ''] when no marker is found.
function splitBodyAndSignature(body) {
  if (!body) return ['', ''];
  const match = /<(div|span|table)[^>]*data-signature\s*=/i.exec(body);
  if (!match) return [body, ''];
  const idx = match.index;
  const prose = body.slice(0, idx).replace(/(<br\s*\/?>(\s|\n)*)+$/i, '').trimEnd();
  const sig = body.slice(idx);
  return [prose, sig];
}

const STAGES = [
  { value: 'FirstContact', label: 'First Contact' },
  { value: 'FollowUp', label: 'Follow-Up' },
  { value: 'Qualification', label: 'Qualification' },
  { value: 'ReEngagement', label: 'Re-Engagement' },
  { value: 'DemoInvite', label: 'Demo Invite' },
  { value: 'Negotiation', label: 'Negotiation' },
  { value: 'Closed', label: 'Closed' },
];

export default function EmailCompositionPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedContactId = searchParams.get('contactId');
  const queueItemId = searchParams.get('queueId');

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [hintDialogOpen, setHintDialogOpen] = useState(false);
  const [hintRefreshing, setHintRefreshing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [aiAssistDialogOpen, setAiAssistDialogOpen] = useState(false);
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);

  // Templates & contacts
  const [templates, setTemplates] = useState([]);
  const [recommendedContacts, setRecommendedContacts] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState(preselectedContactId || '');

  // Email content
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [stage, setStage] = useState('FirstContact');
  const [showPlaceholderPreview, setShowPlaceholderPreview] = useState(false);

  // AI states
  const [improving, setImproving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [aiTaskLabel, setAiTaskLabel] = useState('');
  const aiAbortRef = useRef(null);
  const [improvePreview, setImprovePreview] = useState(null);
  const [translatedPreview, setTranslatedPreview] = useState(null);
  const [targetLang, setTargetLang] = useState('');
  const [translateProvider, setTranslateProvider] = useState('openai');
  const [aiProviders, setAiProviders] = useState([]);
  const [selectedAiProvider, setSelectedAiProvider] = useState('openai');
  const [personalizing, setPersonalizing] = useState(false);
  const [personalizePreview, setPersonalizePreview] = useState(null);
  const [chainRunning, setChainRunning] = useState(false);
  const [chainResult, setChainResult] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [improvePrompts, setImprovePrompts] = useState([]);
  const [selectedImprovePromptId, setSelectedImprovePromptId] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [selectedOpeningTone, setSelectedOpeningTone] = useState('');

  // Template filters
  const PERSONAS = ['CTO', 'CFO', 'ProductManager', 'CEO', 'VP_Engineering', 'VP_Sales', 'Other'];
  const TONES = ['Formal', 'Direct', 'Friendly', 'Empathetic', 'Concise'];
  const COMPANY_SIZES = ['SMB', 'Mid-market', 'Enterprise'];
  const [filterStage, setFilterStage] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterPersona, setFilterPersona] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');

  // Send states
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  // Draft / Schedule states
  const [editingQueueId, setEditingQueueId] = useState(queueItemId ? parseInt(queueItemId) : null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [scheduling, setScheduling] = useState(false);

  const refreshHint = useCallback(async () => {
    if (!leadId) return;
    setHintRefreshing(true);
    try {
      await generateLeadOutreachHint(leadId);
      const leadRes = await getLead(leadId);
      setLead(leadRes.data);
    } catch {
      setError('Failed to refresh outreach hint');
    } finally {
      setHintRefreshing(false);
    }
  }, [leadId]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const leadRes = await getLead(leadId);
      setLead(leadRes.data);

      try {
        const recRes = await getRecommendedContacts(leadId);
        setRecommendedContacts(recRes.data || []);
      } catch { /* non-critical */ }
    } catch {
      setError('Failed to load lead data');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  // Re-fetch templates when filters or lead context changes
  const fetchTemplates = useCallback(async () => {
    try {
      const params = {
        channel: 'Email',
        recommendedForLeadId: parseInt(leadId),
      };
      if (filterStage) params.stage = filterStage;
      if (filterIndustry) params.industry = filterIndustry;
      if (filterPersona) params.persona = filterPersona;
      if (filterLanguage) params.language = filterLanguage;
      const { data } = await getInteractionTemplates(params);
      setTemplates((data || []).filter(t => t.isActive));
    } catch { /* non-fatal */ }
  }, [leadId, filterStage, filterIndustry, filterPersona, filterLanguage]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);


  useEffect(() => {
    if (!queueItemId) return;
    (async () => {
      try {
        const { data } = await getEmailQueueItem(queueItemId);
        if (!data) return;
        setSubject(data.subject || '');
        setBody(data.body || '');
        if (data.contactId) setSelectedContactId(String(data.contactId));
        if (data.templateId) setSelectedTemplateId(String(data.templateId));
        if (data.stage) setStage(data.stage);
        if (data.scheduledFor) {
          const d = new Date(data.scheduledFor);
          d.setSeconds(0, 0);
          const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          setScheduledAt(iso);
        }
        setEditingQueueId(parseInt(queueItemId));
      } catch {
        setError('Failed to load queued email');
      }
    })();
  }, [queueItemId]);

  // Auto-prefill industry filter from lead (try common shapes).
  // Intentionally only depends on `lead`; rerunning on filterIndustry change
  // would re-set after user clears it.
  useEffect(() => {
    const ind = lead?.industryName || lead?.industry?.name || lead?.industry;
    if (ind && typeof ind === 'string' && !filterIndustry) {
      setFilterIndustry(ind);
    }
  }, [lead]); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAiProviders();
        const list = (data || []).filter(p => p.isAvailable);
        setAiProviders(list);
        const openai = list.find(p => (p.name || '').toLowerCase() === 'openai');
        if (openai) {
          setSelectedAiProvider('openai');
          setTranslateProvider('openai');
        } else if (list.length > 0) {
          setSelectedAiProvider(list[0].name);
          setTranslateProvider(list[0].name);
        }
      } catch { /* fallback to default */ }
    })();
  }, []);

  useEffect(() => {
    getCurrentUser().then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  // If body/subject was set before currentUser or contact resolved (race on initial
  // template load), re-resolve any remaining {{...}} tokens once those values land.
  useEffect(() => {
    if (!currentUser && !selectedContactId) return;
    setBody(prev => {
      if (!prev || !/\{\{\w+\}\}/.test(prev)) return prev;
      const sel = (lead?.contacts || []).find(c => String(c.id) === String(selectedContactId));
      return substitutePlaceholdersImpl(prev, lead, sel, currentUser);
    });
    setSubject(prev => {
      if (!prev || !/\{\{\w+\}\}/.test(prev)) return prev;
      const sel = (lead?.contacts || []).find(c => String(c.id) === String(selectedContactId));
      return substitutePlaceholdersImpl(prev, lead, sel, currentUser);
    });
  }, [currentUser, selectedContactId, lead]);

  // Inject the user's email signature into the editor body so it is visible while
  // composing (not just appended at send time). Runs once `currentUser` resolves.
  useEffect(() => {
    const sig = currentUser?.emailSignature;
    if (!sig) return;
    setBody(prev => ensureSignature(prev, sig));
  }, [currentUser]);

  // Load prompts for improvement-related categories
  useEffect(() => {
    const cats = ['ImproveOutreach', 'AdjustTone', 'Shorten', 'Expand', 'PersonalizeTemplate'];
    Promise.all(cats.map(c => getAiPromptTemplatesByCategory(c).catch(() => ({ data: [] }))))
      .then(results => {
        const merged = [];
        results.forEach((r, i) => {
          (r.data || []).filter(t => t.isActive).forEach(t => {
            merged.push({ ...t, _category: cats[i] });
          });
        });
        setImprovePrompts(merged);
      });
  }, []);

  const resolvePlaceholders = useCallback((html) => {
    const sel = (lead?.contacts || []).find(c => String(c.id) === String(selectedContactId));
    return substitutePlaceholdersImpl(html, lead, sel, currentUser);
  }, [lead, selectedContactId, currentUser]);

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    setError('');
    try {
      const { data } = await previewEmail({
        leadId: parseInt(leadId),
        contactId: selectedContactId ? parseInt(selectedContactId) : null,
        templateId: parseInt(templateId),
        stage,
      });
      setSubject(resolvePlaceholders(data.subject || ''));
      setBody(ensureSignature(resolvePlaceholders(data.body || ''), currentUser?.emailSignature));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load template preview');
    }
  };

  const handleContactChange = async (contactId) => {
    setSelectedContactId(contactId);
    if (selectedTemplateId) {
      try {
        const { data } = await previewEmail({
          leadId: parseInt(leadId),
          contactId: contactId ? parseInt(contactId) : null,
          templateId: parseInt(selectedTemplateId),
          stage,
        });
        const sel = (lead?.contacts || []).find(c => String(c.id) === String(contactId));
        setSubject(substitutePlaceholdersImpl(data.subject || '', lead, sel, currentUser));
        setBody(ensureSignature(
          substitutePlaceholdersImpl(data.body || '', lead, sel, currentUser),
          currentUser?.emailSignature
        ));
      } catch { /* keep current content */ }
    }
  };

  const isCanceled = (err) =>
    err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.message === 'canceled';

  const cancelAiTask = () => {
    if (aiAbortRef.current) aiAbortRef.current.abort();
  };

  const handleImprove = async (action) => {
    const label = action === 'shorten' ? 'Shortening message…'
      : action === 'expand' ? 'Expanding message…'
      : 'Improving message…';
    setImproving(true);
    setError('');
    setAiTaskLabel(label);
    const controller = new AbortController();
    aiAbortRef.current = controller;
    try {
      const { data } = await improveEmail({
        leadId: parseInt(leadId),
        contactId: selectedContactId ? parseInt(selectedContactId) : null,
        currentSubject: subject,
        currentBody: body,
        subject,
        body,
        action,
        stage,
        aiProviderName: selectedAiProvider,
        promptTemplateId: selectedImprovePromptId ? parseInt(selectedImprovePromptId) : null,
        customInstructions: customInstructions.trim() || null,
      }, controller.signal);
      const hasBody = !!(data.improvedBody && data.improvedBody.trim());
      const hasSubject = !!(data.improvedSubject && data.improvedSubject.trim());
      if (!hasBody && !hasSubject) {
        setError(data.suggestions || 'AI returned an empty result. Try again or pick a different prompt template.');
      } else {
        setImprovePreview({
          ...data,
          _action: action,
          _originalSubject: subject,
          _originalBody: body,
        });
      }
    } catch (err) {
      if (!isCanceled(err)) setError(err.response?.data?.message || 'AI improvement failed');
    } finally {
      aiAbortRef.current = null;
      setAiTaskLabel('');
      setImproving(false);
    }
  };

  const handleAutoImprove = async () => {
    if (!body.trim()) { setError('Body required'); return; }
    setImproving(true);
    setError('');
    setAiTaskLabel('Auto-improving (Azotte exec voice)…');
    const controller = new AbortController();
    aiAbortRef.current = controller;
    try {
      const { data } = await autoImproveEmail({
        leadId: parseInt(leadId),
        contactId: selectedContactId ? parseInt(selectedContactId) : null,
        subject,
        body,
        aiProviderName: selectedAiProvider,
      }, controller.signal);
      const hasBody = !!(data.improvedBody && data.improvedBody.trim());
      const hasSubject = !!(data.improvedSubject && data.improvedSubject.trim());
      if (!hasBody && !hasSubject) {
        setError(data.suggestions || 'Auto-improve returned an empty result. Try again.');
      } else {
        setImprovePreview({
          ...data,
          _action: 'auto-improve',
          _originalSubject: subject,
          _originalBody: body,
        });
      }
    } catch (err) {
      if (!isCanceled(err)) setError(err.response?.data?.message || err.response?.data || 'Auto-improve failed');
    } finally {
      aiAbortRef.current = null;
      setAiTaskLabel('');
      setImproving(false);
    }
  };

  const handleTranslate = async (langOverride) => {
    const target = (langOverride ?? targetLang).trim();
    if (!target) { setError('Please enter a target language'); return; }
    if (langOverride) setTargetLang(langOverride);
    setTranslating(true);
    setError('');
    setAiTaskLabel(`Translating to ${target}…`);
    const controller = new AbortController();
    aiAbortRef.current = controller;
    try {
      const { data } = await translateEmailPreview({
        subject,
        body,
        targetLanguage: target,
        aiProviderName: translateProvider,
      }, controller.signal);
      setTranslatedPreview({ ...data, _targetLanguage: target });
    } catch (err) {
      if (!isCanceled(err)) setError(err.response?.data?.message || 'Translation failed');
    } finally {
      aiAbortRef.current = null;
      setAiTaskLabel('');
      setTranslating(false);
    }
  };

  const handleUseTranslation = () => {
    if (translatedPreview) {
      setSubject(resolvePlaceholders(translatedPreview.translatedSubject || subject));
      setBody(resolvePlaceholders(translatedPreview.translatedBody || body));
      setTranslatedPreview(null);
    }
  };

  const handlePersonalize = async () => {
    if (!body.trim()) { setError('Body required'); return; }
    setPersonalizing(true);
    setError('');
    setAiTaskLabel('Personalizing for lead…');
    const controller = new AbortController();
    aiAbortRef.current = controller;
    try {
      const { data } = await personalizeEmail({
        leadId: parseInt(leadId),
        contactId: selectedContactId ? parseInt(selectedContactId) : null,
        subject,
        body,
        channel: 'Email',
        aiProviderName: selectedAiProvider,
      }, controller.signal);
      setPersonalizePreview(data);
    } catch (err) {
      if (!isCanceled(err)) {
        const detail = err.response?.data?.errorMessage || err.response?.data?.ErrorMessage
          || err.response?.data?.message || err.response?.data || err.message;
        setError(`Personalize failed: ${detail}`);
      }
    } finally {
      aiAbortRef.current = null;
      setAiTaskLabel('');
      setPersonalizing(false);
    }
  };

  const handleApplyImprove = () => {
    if (improvePreview) {
      setSubject(resolvePlaceholders(improvePreview.improvedSubject || subject));
      setBody(ensureSignature(
        resolvePlaceholders(improvePreview.improvedBody || body),
        currentUser?.emailSignature
      ));
      setImprovePreview(null);
    }
  };

  const handleApplyPersonalize = () => {
    if (personalizePreview) {
      setSubject(resolvePlaceholders(personalizePreview.personalizedSubject || subject));
      setBody(resolvePlaceholders(personalizePreview.personalizedBody || body));
      setPersonalizePreview(null);
    }
  };

  const handleRunChain = async () => {
    const tpl = templates.find(t => String(t.id) === String(selectedTemplateId));
    if (!tpl?.enrichmentChain) { setError('Template has no chain'); return; }
    let chain = [];
    try {
      chain = JSON.parse(tpl.enrichmentChain);
      if (!Array.isArray(chain)) throw new Error();
    } catch {
      setError('Template chain JSON invalid');
      return;
    }
    if (chain.length === 0) { setError('Chain empty'); return; }
    setChainRunning(true);
    setChainResult(null);
    setError('');
    setAiTaskLabel(`Running auto-pipeline (${chain.length} steps)…`);
    const controller = new AbortController();
    aiAbortRef.current = controller;
    try {
      const { data } = await runEmailChain({
        leadId: parseInt(leadId),
        contactId: selectedContactId ? parseInt(selectedContactId) : null,
        subject,
        body,
        chain,
        targetLanguage: targetLang || null,
        channel: 'Email',
        aiProviderName: selectedAiProvider,
      }, controller.signal);
      setChainResult(data);
    } catch (err) {
      if (!isCanceled(err)) {
        const detail = err.response?.data?.errorMessage || err.response?.data?.ErrorMessage
          || err.response?.data?.message || err.response?.data || err.message;
        setError(`Chain failed: ${detail}`);
      }
    } finally {
      aiAbortRef.current = null;
      setAiTaskLabel('');
      setChainRunning(false);
    }
  };

  const handleApplyChain = () => {
    if (chainResult) {
      setSubject(resolvePlaceholders(chainResult.finalSubject || subject));
      setBody(resolvePlaceholders(chainResult.finalBody || body));
      setChainResult(null);
    }
  };

  const handleSend = async () => {
    if (!selectedContactId) { setError('Please select a contact'); return; }
    if (!subject.trim()) { setError('Subject is required'); return; }
    if (!body.trim()) { setError('Body is required'); return; }
    setSending(true);
    setError('');
    setSuccess('');
    try {
      // If editing an existing draft/scheduled queue item, send via queue endpoint
      // so the same row is dispatched (instead of creating a duplicate).
      if (editingQueueId) {
        // Persist any edits first as a draft, then send-now.
        await saveEmailDraft({
          id: editingQueueId,
          leadId: parseInt(leadId),
          contactId: parseInt(selectedContactId),
          subject,
          body,
          stage,
          templateId: selectedTemplateId ? parseInt(selectedTemplateId) : null,
        });
        await sendQueuedEmailNow(editingQueueId);
      } else {
        await sendEmail({
          leadId: parseInt(leadId),
          contactId: parseInt(selectedContactId),
          subject,
          body,
          stage,
          templateId: selectedTemplateId ? parseInt(selectedTemplateId) : null,
        });
      }
      setSuccess('Email sent successfully!');
      setConfirmSend(false);
      setTimeout(() => navigate(`/leads/${leadId}?tab=activity`), 2000);
    } catch (err) {
      const data = err.response?.data;
      const detail =
        data?.errorMessage ||
        data?.ErrorMessage ||
        data?.message ||
        data?.title ||
        (typeof data === 'string' ? data : null) ||
        err.message ||
        'Failed to send email';
      console.error('Send email failed:', err.response?.status, data);
      setError(`Failed to send email: ${detail}`);
      setConfirmSend(false);
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!subject.trim() && !body.trim()) {
      setError('Subject or body required to save a draft');
      return;
    }
    setSavingDraft(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await saveEmailDraft({
        id: editingQueueId || undefined,
        leadId: parseInt(leadId),
        contactId: selectedContactId ? parseInt(selectedContactId) : null,
        subject,
        body,
        stage,
        templateId: selectedTemplateId ? parseInt(selectedTemplateId) : null,
      });
      setEditingQueueId(data.id);
      setSuccess('Draft saved');
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSchedule = async () => {
    if (!selectedContactId) { setError('Please select a contact'); return; }
    if (!subject.trim()) { setError('Subject is required'); return; }
    if (!body.trim()) { setError('Body is required'); return; }
    if (!scheduledAt) { setError('Pick a date/time'); return; }
    const whenLocal = new Date(scheduledAt);
    if (isNaN(whenLocal.getTime()) || whenLocal.getTime() <= Date.now()) {
      setError('Scheduled time must be in the future');
      return;
    }
    setScheduling(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await scheduleEmail({
        id: editingQueueId || undefined,
        leadId: parseInt(leadId),
        contactId: parseInt(selectedContactId),
        subject,
        body,
        stage,
        templateId: selectedTemplateId ? parseInt(selectedTemplateId) : null,
        scheduledFor: whenLocal.toISOString(),
      });
      setEditingQueueId(data.id);
      setScheduleDialogOpen(false);
      setSuccess(`Scheduled for ${whenLocal.toLocaleString()}`);
      setTimeout(() => navigate(`/leads/${leadId}?tab=activity`), 1500);
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to schedule');
    } finally {
      setScheduling(false);
    }
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!lead) return <Box sx={{ p: 5, textAlign: 'center' }}><Typography>Lead not found</Typography></Box>;

  const contacts = lead.contacts || [];
  const selectedContact = contacts.find(c => String(c.id) === String(selectedContactId));

  const substitutePlaceholders = (html) => substitutePlaceholdersImpl(html, lead, selectedContact, currentUser);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/leads/${leadId}`)}>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Back to {lead.companyName}</Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Back</Box>
        </Button>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small" variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={() => setAiAssistDialogOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 1.5,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>AI Assist</Box>
          </Button>
          <Button
            size="small" variant="outlined"
            startIcon={<TranslateIcon />}
            onClick={() => setTranslateDialogOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 1.5, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Translate</Box>
          </Button>
          <Tooltip title="Outreach hint">
            <Button
              size="small" variant="outlined"
              startIcon={<LightbulbIcon sx={{ fontSize: 18, color: '#ea580c' }} />}
              onClick={() => setHintDialogOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 1.5, borderColor: '#fed7aa', color: '#9a3412', fontWeight: 600, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Hint</Box>
            </Button>
          </Tooltip>
          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
              sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, width: 36, height: 36 }}
            >
              <MoreVertIcon sx={{ fontSize: 18, color: '#475569' }} />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}>
            <MenuItem onClick={() => { setMoreMenuAnchor(null); setHintDialogOpen(true); }} dense>
              <ListItemIcon><LightbulbIcon fontSize="small" sx={{ color: '#ea580c' }} /></ListItemIcon>
              <ListItemText primary="View outreach hint" />
            </MenuItem>
            <MenuItem onClick={() => { setMoreMenuAnchor(null); refreshHint(); }} disabled={hintRefreshing} dense>
              <ListItemIcon>{hintRefreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={hintRefreshing ? 'Refreshing hint…' : 'Refresh outreach hint'} />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setMoreMenuAnchor(null); navigate(`/leads/${leadId}`); }} dense>
              <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Open lead detail" />
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Compose Email</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        to {lead.companyName}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#f7fafc', display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>From (sender):</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {currentUser?.fullName || currentUser?.username || 'Loading...'}
            {currentUser?.jobTitle && ` — ${currentUser.jobTitle}`}
          </Typography>
          {currentUser?.email && (
            <Typography variant="caption" color="text.secondary">{currentUser.email}</Typography>
          )}
          {(!currentUser?.fullName || !currentUser?.jobTitle) && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              {!currentUser?.fullName && 'Set Full Name'}
              {!currentUser?.fullName && !currentUser?.jobTitle && ' + '}
              {!currentUser?.jobTitle && 'Set Job Title'}
              {' '}in Profile to fill {'{{SenderName}}'} / {'{{SenderTitle}}'} placeholders.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Main editor */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Contact *"
                  value={selectedContactId}
                  onChange={e => handleContactChange(e.target.value)}
                >
                  <MenuItem value="">— Select Contact —</MenuItem>
                  {(recommendedContacts.length > 0 ? recommendedContacts : contacts).map(c => {
                    const id = c.id ?? c.contactId;
                    const name = c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim();
                    const title = c.jobTitle;
                    const score = c.relevanceScore ?? c.score;
                    return (
                      <MenuItem key={id} value={id}>
                        {name || '(no name)'}
                        {title ? ` — ${title}` : ''}
                        {score ? ` (score ${score})` : ''}
                      </MenuItem>
                    );
                  })}
                </TextField>
                {selectedContact && selectedContact.email && (
                  <Typography variant="caption" color="text.secondary">To: {selectedContact.email}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Stage"
                  value={stage}
                  onChange={e => setStage(e.target.value)}
                >
                  {STAGES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              {(() => {
                const activeCount = [filterStage, filterIndustry, filterPersona, filterLanguage].filter(Boolean).length;
                return (
                  <Paper variant="outlined" sx={{ borderRadius: 1.5, mb: 1, overflow: 'hidden' }}>
                    {/* Header (clickable) */}
                    <Stack
                      direction="row" alignItems="center" spacing={1}
                      onClick={() => setFiltersOpen(o => !o)}
                      sx={{
                        px: 1.5, py: 1, cursor: 'pointer', userSelect: 'none',
                        bgcolor: filtersOpen ? '#f8fafc' : 'transparent',
                        '&:hover': { bgcolor: '#f8fafc' },
                      }}
                    >
                      <FilterListIcon sx={{ fontSize: 18, color: '#475569' }} />
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>
                        Template filters
                      </Typography>
                      {activeCount > 0 && (
                        <Chip size="small" label={activeCount}
                          sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca' }} />
                      )}
                      <Box sx={{ flex: 1 }} />
                      {activeCount > 0 && !filtersOpen && (
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', maxWidth: '60%', justifyContent: 'flex-end' }}>
                          {filterStage && <Chip size="small" label={`Stage: ${filterStage}`} onDelete={(e) => { e.stopPropagation(); setFilterStage(''); }} sx={{ height: 20 }} />}
                          {filterIndustry && <Chip size="small" label={`Industry: ${filterIndustry}`} onDelete={(e) => { e.stopPropagation(); setFilterIndustry(''); }} sx={{ height: 20 }} />}
                          {filterPersona && <Chip size="small" label={`Persona: ${filterPersona}`} onDelete={(e) => { e.stopPropagation(); setFilterPersona(''); }} sx={{ height: 20 }} />}
                          {filterLanguage && <Chip size="small" label={`Lang: ${filterLanguage}`} onDelete={(e) => { e.stopPropagation(); setFilterLanguage(''); }} sx={{ height: 20 }} />}
                        </Stack>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setFiltersOpen(o => !o); }}
                        sx={{ transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    {/* Body */}
                    <Collapse in={filtersOpen} unmountOnExit>
                      <Box sx={{ p: 1.5, borderTop: '1px solid #e2e8f0' }}>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                          <TextField select size="small" label="Stage filter" value={filterStage}
                            onChange={e => setFilterStage(e.target.value)} sx={{ minWidth: 140 }}>
                            <MenuItem value="">Any stage</MenuItem>
                            {STAGES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                          </TextField>
                          <TextField size="small" label="Industry" value={filterIndustry}
                            onChange={e => setFilterIndustry(e.target.value)} sx={{ minWidth: 140 }}
                            placeholder="e.g. SaaS" />
                          <TextField select size="small" label="Persona" value={filterPersona}
                            onChange={e => setFilterPersona(e.target.value)} sx={{ minWidth: 140 }}>
                            <MenuItem value="">Any</MenuItem>
                            {PERSONAS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                          </TextField>
                          <TextField size="small" label="Language" value={filterLanguage}
                            onChange={e => setFilterLanguage(e.target.value)} sx={{ minWidth: 100 }}
                            placeholder="en" />
                          {activeCount > 0 && (
                            <Button size="small" onClick={() => {
                              setFilterStage(''); setFilterIndustry(''); setFilterPersona(''); setFilterLanguage('');
                            }}>Clear filters</Button>
                          )}
                        </Stack>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })()}

              {/* Opening picker (when template has openings) */}
              {(() => {
                const tpl = templates.find(t => String(t.id) === String(selectedTemplateId));
                let openings = [];
                if (tpl?.openings) {
                  try {
                    const parsed = JSON.parse(tpl.openings);
                    if (Array.isArray(parsed)) openings = parsed;
                  } catch {}
                }
                if (openings.length === 0) return null;
                return (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Opening (by tone)"
                    value={selectedOpeningTone}
                    onChange={(e) => {
                      const tone = e.target.value;
                      setSelectedOpeningTone(tone);
                      const opening = openings.find(o => o.tone === tone);
                      if (opening?.text) {
                        const cleaned = body.replace(/<p data-opening="[^"]*">[\s\S]*?<\/p>/, '');
                        const merged = `<p data-opening="${tone}">${opening.text}</p>` + cleaned;
                        setBody(resolvePlaceholders(merged));
                      }
                    }}
                    sx={{ mb: 1.5 }}
                    helperText="Replaces / prepends the opening line based on tone"
                  >
                    <MenuItem value="">— No opening —</MenuItem>
                    {openings.map((o, i) => (
                      <MenuItem key={i} value={o.tone}>
                        <Chip size="small" label={o.tone} sx={{ mr: 1, height: 20 }} />
                        {(o.text || '').slice(0, 60)}{(o.text || '').length > 60 ? '...' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })()}

              <TextField
                select
                size="small"
                fullWidth
                label="Template"
                value={selectedTemplateId}
                onChange={e => handleTemplateSelect(e.target.value)}
                helperText={templates.length === 0 ? 'No templates match. Adjust filters or create one in Management.' : `${templates.length} template(s)`}
              >
                <MenuItem value="">— No Template (Blank) —</MenuItem>
                {(() => {
                  const recommended = templates.filter(t => (t.matchScore ?? 0) >= 10);
                  const others = templates.filter(t => (t.matchScore ?? 0) < 10);
                  const items = [];
                  if (recommended.length > 0) {
                    items.push(<ListSubheader key="rec-h">★ Recommended for {lead.companyName}</ListSubheader>);
                    recommended.forEach(t => items.push(
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                        {t.stage && <Chip size="small" label={t.stage} sx={{ ml: 1, height: 18 }} variant="outlined" />}
                        {t.persona && <Chip size="small" label={t.persona} sx={{ ml: 0.5, height: 18 }} variant="outlined" />}
                        {t.matchScore != null && <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: '0.75rem' }}>match {t.matchScore}</span>}
                      </MenuItem>
                    ));
                  }
                  if (others.length > 0) {
                    items.push(<ListSubheader key="all-h">All matching templates</ListSubheader>);
                    others.forEach(t => items.push(
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                        {t.stage && <Chip size="small" label={t.stage} sx={{ ml: 1, height: 18 }} variant="outlined" />}
                      </MenuItem>
                    ));
                  }
                  return items;
                })()}
              </TextField>
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                label="Subject *"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Email subject line"
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Body *</Typography>
                <Button
                  size="small"
                  onClick={() => setShowPlaceholderPreview(!showPlaceholderPreview)}
                  startIcon={showPlaceholderPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                >
                  {showPlaceholderPreview ? 'Hide preview' : 'Preview placeholders'}
                </Button>
              </Box>

              <Grid container spacing={1.5} alignItems="stretch">
                <Grid size={{ xs: 12, md: showPlaceholderPreview ? 6 : 12 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <RichEmailEditor value={body} onChange={setBody} minHeight={380} />
                </Grid>
                {showPlaceholderPreview && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ px: 1.5, py: 1, bgcolor: '#f7fafc', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          Rendered preview — placeholders substituted
                        </Typography>
                        {subject && (
                          <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                            Subject: {substitutePlaceholders(subject)}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <iframe
                          title="Email preview"
                          srcDoc={buildPreviewDoc(substitutePlaceholders(body))}
                          sandbox=""
                          style={{ width: '100%', height: '100%', border: 'none', display: 'block', backgroundColor: '#fff' }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => setConfirmSend(true)}
                disabled={sending || !selectedContactId}
              >
                Send Email
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                onClick={handleSaveDraft}
                disabled={savingDraft}
              >
                {savingDraft ? 'Saving...' : (editingQueueId ? 'Update Draft' : 'Save Draft')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ScheduleSendOutlinedIcon />}
                onClick={() => setScheduleDialogOpen(true)}
                disabled={!selectedContactId}
              >
                Schedule
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12 }}>
          {/* AI Improve Dialog */}
          <Dialog open={aiAssistDialogOpen} onClose={() => setAiAssistDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoFixHighIcon sx={{ color: '#6366f1' }} /> AI Assistant
            </DialogTitle>
            <DialogContent dividers>
            <TextField
              select
              size="small"
              fullWidth
              label="LLM Provider"
              value={selectedAiProvider}
              onChange={(e) => setSelectedAiProvider(e.target.value)}
              sx={{ mb: 1.5 }}
              helperText={aiProviders.length === 0 ? 'No providers configured. Set one in Management → AI Settings.' : null}
            >
              {aiProviders.length === 0 ? (
                <MenuItem value="openai">OpenAI (default)</MenuItem>
              ) : (
                aiProviders.map(p => (
                  <MenuItem key={p.name} value={p.name}>
                    {p.displayName}{p.isDefault ? ' (default)' : ''}
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              select
              size="small"
              fullWidth
              label="Improvement prompt"
              value={selectedImprovePromptId}
              onChange={(e) => setSelectedImprovePromptId(e.target.value)}
              sx={{ mb: 1.5 }}
              helperText={improvePrompts.length === 0 ? 'No prompts. Add some in Management → AI Prompts.' : 'Used by Improve / Shorten / Expand'}
            >
              <MenuItem value="">— Default per category —</MenuItem>
              {(() => {
                const grouped = improvePrompts.reduce((acc, p) => {
                  (acc[p._category] ||= []).push(p);
                  return acc;
                }, {});
                const items = [];
                Object.keys(grouped).forEach(cat => {
                  items.push(<ListSubheader key={`h-${cat}`}>{cat}</ListSubheader>);
                  grouped[cat].forEach(p => items.push(
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}{p.isDefault ? ' ★' : ''}
                    </MenuItem>
                  ));
                });
                return items;
              })()}
            </TextField>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              label="Custom instructions"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder='e.g. "Mention our new pricing tier", "Sound more urgent", "Add a P.S."'
              helperText="Appended to every Improve / Shorten / Expand call"
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Tooltip title="One-click rewrite into Azotte's executive, peer-to-peer voice. Uses the lead's strategic brief (AI insights) and product brief. Ignores prompt template / custom instructions above.">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleAutoImprove}
                  disabled={improving || !body.trim()}
                  sx={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                    color: '#fff',
                    '&:hover': { background: 'linear-gradient(135deg, #020617 0%, #1e293b 100%)' },
                  }}
                >
                  {improving && aiTaskLabel.startsWith('Auto-improving') ? 'Auto-improving…' : 'Auto-Improve (Azotte voice)'}
                </Button>
              </Tooltip>
              <Tooltip title="Rewrites the email using lead industry, profile, signals, and product brief context. Lead-aware enrichment.">
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handlePersonalize}
                  disabled={personalizing || !body.trim()}
                >
                  {personalizing ? 'Personalizing...' : 'Personalize for Lead'}
                </Button>
              </Tooltip>
              {(() => {
                const tpl = templates.find(t => String(t.id) === String(selectedTemplateId));
                if (tpl?.enrichmentChain) {
                  let chainArr = [];
                  try { chainArr = JSON.parse(tpl.enrichmentChain); } catch {}
                  if (Array.isArray(chainArr) && chainArr.length > 0) {
                    return (
                      <Tooltip title={`Runs template chain: ${chainArr.join(' → ')}`}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AutoFixHighIcon />}
                          onClick={handleRunChain}
                          disabled={chainRunning || !body.trim()}
                        >
                          {chainRunning ? 'Running chain...' : `Run Auto-Pipeline (${chainArr.length} steps)`}
                        </Button>
                      </Tooltip>
                    );
                  }
                }
                return null;
              })()}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoFixHighIcon />}
                onClick={() => handleImprove('improve')}
                disabled={improving || !body.trim()}
              >
                {improving ? 'Working...' : 'Improve Message'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCutIcon />}
                onClick={() => handleImprove('shorten')}
                disabled={improving || !body.trim()}
              >
                Shorten
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditNoteIcon />}
                onClick={() => handleImprove('expand')}
                disabled={improving || !body.trim()}
              >
                Expand
              </Button>
            </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAiAssistDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Translation Dialog */}
          <Dialog open={translateDialogOpen} onClose={() => setTranslateDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TranslateIcon sx={{ color: '#0ea5e9' }} /> Translation Preview
            </DialogTitle>
            <DialogContent dividers>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              For your understanding only — translated copy is not sent.
            </Typography>

            {(() => {
              const savedLangs = (currentUser?.preferredLanguages || '')
                .split(',').map(s => s.trim()).filter(Boolean);
              if (savedLangs.length === 0) return null;
              return (
                <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                  {savedLangs.map(l => (
                    <Chip
                      key={l}
                      label={l}
                      size="small"
                      onClick={() => handleTranslate(l)}
                      disabled={translating || !body.trim()}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
              );
            })()}

            <Box sx={{ mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={targetLang}
                onChange={e => setTargetLang(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleTranslate(); } }}
                placeholder="e.g. French, Spanish, Hebrew"
              />
            </Box>
            {aiProviders.length > 1 && (
              <TextField
                select
                size="small"
                fullWidth
                label="Translate provider"
                value={translateProvider}
                onChange={e => setTranslateProvider(e.target.value)}
                sx={{ mb: 1 }}
                helperText="Defaults to OpenAI for translation quality"
              >
                {aiProviders.map(p => (
                  <MenuItem key={p.name} value={p.name}>
                    {p.displayName}{p.isDefault ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<TranslateIcon />}
              onClick={() => handleTranslate()}
              disabled={translating || !body.trim()}
            >
              {translating ? 'Translating...' : 'Translate Preview'}
            </Button>

            {translatedPreview && (() => {
              const lang = translatedPreview._targetLanguage || targetLang;
              const rtl = isRtlLanguage(lang);
              return (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                    <Chip size="small" label={lang} />
                    {rtl && <Chip size="small" color="info" label="RTL" />}
                    <Typography variant="caption" color="text.secondary">
                      via {translatedPreview.aiProviderUsed || translateProvider}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Translated Subject:</Typography>
                  <Typography
                    sx={{ fontWeight: 500, mb: 1, direction: rtl ? 'rtl' : 'ltr', textAlign: rtl ? 'right' : 'left' }}
                  >
                    {translatedPreview.translatedSubject}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Translated Body:</Typography>
                  <Box
                    dir={rtl ? 'rtl' : 'ltr'}
                    sx={{
                      fontSize: '0.9rem',
                      maxHeight: 240,
                      overflow: 'auto',
                      direction: rtl ? 'rtl' : 'ltr',
                      textAlign: rtl ? 'right' : 'left',
                      '& *': rtl ? { direction: 'rtl', textAlign: 'right' } : {},
                    }}
                    dangerouslySetInnerHTML={{ __html: translatedPreview.translatedBody }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button variant="contained" size="small" onClick={handleUseTranslation}>Use Translation</Button>
                    <Button variant="outlined" size="small" onClick={() => setTranslatedPreview(null)}>Dismiss</Button>
                  </Box>
                </Paper>
              );
            })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTranslateDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Contact Info */}
          {selectedContact && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact Info</Typography>
              <Box sx={{ fontSize: '0.85rem' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedContact.firstName} {selectedContact.lastName}</Typography>
                {selectedContact.jobTitle && <Typography variant="body2" color="text.secondary">{selectedContact.jobTitle}</Typography>}
                {selectedContact.email && <Typography variant="body2">{selectedContact.email}</Typography>}
                {selectedContact.linkedInUrl && (
                  <Typography variant="body2">
                    <a href={selectedContact.linkedInUrl} target="_blank" rel="noreferrer">LinkedIn</a>
                  </Typography>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Schedule Modal */}
      {scheduleDialogOpen && (
        <Modal title="Schedule Email" onClose={() => setScheduleDialogOpen(false)} maxWidth="xs">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pick when to send this email to <strong>{selectedContact?.firstName} {selectedContact?.lastName}</strong>.
          </Typography>
          <TextField
            type="datetime-local"
            label="Send at"
            fullWidth
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Time is interpreted in your local timezone. Worker polls ~30s.
          </Typography>
          <DialogActions sx={{ px: 0, pb: 0, mt: 2 }}>
            <Button variant="outlined" onClick={() => setScheduleDialogOpen(false)} disabled={scheduling}>Cancel</Button>
            <Button variant="contained" onClick={handleSchedule} disabled={scheduling}>
              {scheduling ? 'Scheduling...' : (editingQueueId ? 'Reschedule' : 'Schedule')}
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* Send Confirmation Modal */}
      {confirmSend && (
        <Modal title="Confirm Send" onClose={() => setConfirmSend(false)} maxWidth="xs">
          <Typography sx={{ mb: 1 }}>
            Send this email to <strong>{selectedContact?.firstName} {selectedContact?.lastName}</strong> at <strong>{selectedContact?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Subject: {subject}
          </Typography>
          <DialogActions sx={{ px: 0, pb: 0 }}>
            <Button variant="outlined" onClick={() => setConfirmSend(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* Improve / Shorten / Expand Preview Modal */}
      {improvePreview && (
        <Modal
          title={
            improvePreview._action === 'shorten' ? 'Shorten Preview'
            : improvePreview._action === 'expand' ? 'Expand Preview'
            : improvePreview._action === 'auto-improve' ? 'Auto-Improve (Azotte voice) — Preview'
            : 'Improve Preview'
          }
          onClose={() => setImprovePreview(null)}
          maxWidth="md">
          <Typography variant="caption" color="text.secondary">
            Provider: {improvePreview.aiProviderUsed || selectedAiProvider}
          </Typography>
          {improvePreview.changesApplied?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 2, bgcolor: '#f7fafc' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Changes applied:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {improvePreview.changesApplied.map((c, i) => (
                  <Typography component="li" key={i} variant="body2">{c}</Typography>
                ))}
              </Box>
            </Paper>
          )}
          {improvePreview.suggestions && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>{improvePreview.suggestions}</Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Original subject:</Typography>
              <Typography sx={{ mb: 1.5, fontSize: '0.9rem' }}>{improvePreview._originalSubject || '-'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Original body:</Typography>
              <Box sx={{ maxHeight: 320, overflow: 'auto', p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1, fontSize: '0.85rem' }}
                dangerouslySetInnerHTML={{ __html: improvePreview._originalBody || '' }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>New subject:</Typography>
              <Typography sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.9rem' }}>{improvePreview.improvedSubject || '-'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>New body:</Typography>
              <Box sx={{ maxHeight: 320, overflow: 'auto', p: 1.5, border: '1px solid #38a169', borderRadius: 1, bgcolor: '#f0fff4', fontSize: '0.85rem' }}
                dangerouslySetInnerHTML={{ __html: improvePreview.improvedBody || '' }} />
            </Grid>
          </Grid>
          <DialogActions sx={{ px: 0, pt: 2 }}>
            <Button variant="outlined" onClick={() => setImprovePreview(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleApplyImprove}>Apply</Button>
          </DialogActions>
        </Modal>
      )}

      {/* Personalize Preview Modal */}
      {personalizePreview && (
        <Modal title="Personalize Preview" onClose={() => setPersonalizePreview(null)} maxWidth="md">
          <Typography variant="caption" color="text.secondary">
            Provider: {personalizePreview.aiProviderUsed}
          </Typography>
          {personalizePreview.changesApplied?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 2, bgcolor: '#f7fafc' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Changes applied:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {personalizePreview.changesApplied.map((c, i) => (
                  <Typography component="li" key={i} variant="body2">{c}</Typography>
                ))}
              </Box>
            </Paper>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>New subject:</Typography>
          <Typography sx={{ fontWeight: 500, mb: 1.5 }}>{personalizePreview.personalizedSubject}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>New body:</Typography>
          <Box sx={{ maxHeight: 320, overflow: 'auto', p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}
            dangerouslySetInnerHTML={{ __html: personalizePreview.personalizedBody }} />
          <DialogActions sx={{ px: 0, pt: 2 }}>
            <Button variant="outlined" onClick={() => setPersonalizePreview(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleApplyPersonalize}>Apply</Button>
          </DialogActions>
        </Modal>
      )}

      {/* Chain Result Modal */}
      {chainResult && (
        <Modal title="Auto-Pipeline Result" onClose={() => setChainResult(null)} maxWidth="md">
          <Typography variant="caption" color="text.secondary">
            Provider: {chainResult.aiProviderUsed}
          </Typography>
          {chainResult.steps?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 2, bgcolor: '#f7fafc' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Steps:</Typography>
              <Stack spacing={0.5}>
                {chainResult.steps.map((s, i) => (
                  <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Chip size="small" label={s.success ? 'OK' : 'FAIL'} color={s.success ? 'success' : 'error'} />
                    <Typography variant="body2">{s.category}</Typography>
                    <Typography variant="caption" color="text.secondary">({s.durationMs}ms)</Typography>
                    {s.error && <Typography variant="caption" color="error">{s.error}</Typography>}
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Final subject:</Typography>
          <Typography sx={{ fontWeight: 500, mb: 1.5 }}>{chainResult.finalSubject}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Final body:</Typography>
          <Box sx={{ maxHeight: 320, overflow: 'auto', p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}
            dangerouslySetInnerHTML={{ __html: chainResult.finalBody }} />
          <DialogActions sx={{ px: 0, pt: 2 }}>
            <Button variant="outlined" onClick={() => setChainResult(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleApplyChain}>Apply</Button>
          </DialogActions>
        </Modal>
      )}

      {/* AI TASK PROGRESS MODAL — blocks all interaction while AI runs, allows cancel */}
      {!!aiTaskLabel && (
        <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth>
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2, color: '#6366f1' }} />
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>{aiTaskLabel}</Typography>
            <Typography variant="body2" color="text.secondary">
              Calling the AI provider. This usually takes 5–30 seconds.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button variant="outlined" color="error" onClick={cancelAiTask}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}


      {/* Outreach Hint Dialog */}
      <Dialog open={hintDialogOpen} onClose={() => setHintDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)' }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '50%',
            bgcolor: '#fff7ed', border: '1px solid #fed7aa',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LightbulbIcon sx={{ color: '#ea580c', fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>Next Outreach Hint</Typography>
            {(lead?.nextOutreachHintUpdatedAt || lead?.nextOutreachHintProvider) && (
              <Typography variant="caption" color="text.secondary">
                {lead?.nextOutreachHintProvider}
                {lead?.nextOutreachHintUpdatedAt && ` · ${new Date(lead.nextOutreachHintUpdatedAt).toLocaleString()}`}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {lead?.nextOutreachHint ? (
            <ComposeHintBody text={lead.nextOutreachHint} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No hint yet. Click Refresh to generate from latest activity.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={refreshHint}
            disabled={hintRefreshing}
            startIcon={hintRefreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none' }}
          >
            {hintRefreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button onClick={() => setHintDialogOpen(false)} variant="contained" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function parseComposeHint(text) {
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const items = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^[*\-•]\s*\*\*([^*]+?):?\*\*\s*(.*)$/);
    if (m) { items.push({ label: m[1].trim(), value: m[2].trim() }); continue; }
    const m2 = line.match(/^[*\-•]\s+(.*)$/);
    if (m2) { items.push({ label: null, value: m2[1].replace(/\*\*/g, '').trim() }); continue; }
    items.push({ label: null, value: line.replace(/\*\*/g, '') });
  }
  return items;
}

function ComposeHintBody({ text }) {
  const items = parseComposeHint(text);
  const dim = (v) => /^(n\/?a|none|wait|watch only|tbd|unknown)$/i.test((v || '').trim());
  return (
    <Stack spacing={1.25}>
      {items.map((it, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1.25 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: dim(it.value) ? '#fdba74' : '#ea580c',
            mt: 0.85, flexShrink: 0,
          }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {it.label && (
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9a3412', letterSpacing: 0.3, textTransform: 'uppercase', lineHeight: 1.2 }}>
                {it.label}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: dim(it.value) ? '#9a3412' : '#1f2937',
                fontStyle: dim(it.value) ? 'italic' : 'normal',
                opacity: dim(it.value) ? 0.7 : 1,
                fontWeight: it.label ? 500 : 400,
                whiteSpace: 'pre-wrap',
              }}
            >
              {it.value || '—'}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
